import moment from 'moment'
import { QbsTable } from 'qbs-react-grid'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { TableColumns } from '../../../common/types'
import Icons from '../../../components/common/icons'
import TabularLoader from '../../../components/common/tabularLoader'
import { calcWindowHeight } from '../../../utilities/calcHeight'
import { getAccreditationDeadlineReport } from '../api'

const defaultColumnProps = { sortable: false, resizable: true, isVisible: true }
const dateColumn = (title: string, field: string): TableColumns => ({
  title,
  field,
  colWidth: 180,
  ...defaultColumnProps,
  customCell: true,
  renderCell: (row) => ({
    cell: <>{row[field] ? moment(row[field]).format('DD-MM-YYYY') : '--'}</>,
  }),
})

const columns: TableColumns[] = [
  {
    title: 'Organisation',
    field: 'organisation',
    colWidth: 240,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (row) => ({
      cell: (
        <Link
          className="text-primary"
          to={`/organisation/${row.organisation_id}/profile`}
        >
          {row.organisation}
        </Link>
      ),
    }),
  },
  {
    title: 'Current Tier',
    field: 'tier',
    colWidth: 130,
    ...defaultColumnProps,
  },
  {
    title: 'Attention',
    field: 'attention',
    colWidth: 250,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (row) => ({
      cell: (
        <Link
          className={row.overdue_days > 0 ? 'text-danger' : 'text-primary'}
          to={`/accreditation/${row.accreditation_id}`}
        >
          {row.attention}
        </Link>
      ),
    }),
  },
  dateColumn('Attention Date', 'deadline_date'),
  {
    title: 'Days Remaining',
    field: 'days_remaining',
    colWidth: 150,
    ...defaultColumnProps,
  },
  {
    title: 'Overdue Days',
    field: 'overdue_days',
    colWidth: 140,
    ...defaultColumnProps,
  },
  {
    title: 'Accreditation Status',
    field: 'accreditation_status',
    colWidth: 190,
    ...defaultColumnProps,
  },
  dateColumn('Accredited On', 'accredited_on'),
  dateColumn('Accreditation Expiry', 'expiry_date'),
  {
    title: 'Report Period (Months)',
    field: 'progress_report_period_months',
    colWidth: 200,
    ...defaultColumnProps,
  },
  dateColumn('Report Window Opens', 'progress_report_window_opens'),
  dateColumn('Progress Report Due', 'progress_report_due_date'),
  {
    title: 'Report Status',
    field: 'progress_report_status',
    colWidth: 150,
    ...defaultColumnProps,
  },
  dateColumn('Renewal Window Opens', 'renewal_due_date'),
  {
    title: 'Application Status',
    field: 'application_status',
    colWidth: 210,
    ...defaultColumnProps,
  },
]

export default function AccreditationDeadlineReport({
  searchParams,
  pageParams,
  onChangePage,
  onChangeRowsPerPage,
  handleSeach,
}: any) {
  const [data, setData] = useState<any>({ items: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    getAccreditationDeadlineReport({
      ...searchParams,
      page: pageParams.page,
      page_size: pageParams.page_size,
      search: pageParams.search,
    })
      .then((response) => {
        if (active) setData(response)
      })
      .catch(() => {
        if (active) setError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [pageParams, searchParams])

  if (loading) return <TabularLoader />
  if (error)
    return (
      <p role="alert" className="p-4 text-danger">
        Unable to load accreditation deadlines. Please try again.
      </p>
    )

  return (
    <div className="p-4">
      <QbsTable
        data={Array.isArray(data) ? data : (data?.items ?? [])}
        dataRowKey="id"
        columns={columns}
        toolbar
        height={calcWindowHeight(335)}
        emptyTitle="No accreditation deadlines"
        emptySubTitle="No organisations match the selected deadline filters."
        pagination
        paginationProps={{
          onPagination: onChangePage,
          total: Array.isArray(data) ? data.length : (data?.total ?? 0),
          currentPage: pageParams?.page,
          rowsPerPage: Number(pageParams?.page_size ?? 30),
          onRowsPerPage: onChangeRowsPerPage,
          dropOptions: [10, 20, 30, 50, 100],
        }}
        renderSortIcon={() => <Icons name="qbs-sort-icon" />}
        searchValue={pageParams?.search}
        onSearch={handleSeach}
        asyncSearch
        columnToggle
      />
    </div>
  )
}
